import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Linkedin } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import TableOfContents from '../components/TableOfContents';
import SocialShare from '../components/SocialShare';
import { useBlog } from '../contexts/BlogContext';
import { generateTOC, addHeadingIds, cleanEmptyParagraphs } from '../utils/tocGenerator';

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { getPostBySlug, posts, loading } = useBlog();
  const navigate = useNavigate();
  const [processedContent, setProcessedContent] = useState('');
  const [tocItems, setTocItems] = useState<any[]>([]);

  const post = getPostBySlug(slug || '');

  useEffect(() => {
    if (!loading && !post) {
      navigate('/blog');
    }
  }, [post, navigate, loading]);

  const processTLDR = (content: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    const headings = tempDiv.querySelectorAll('h2');
    headings.forEach((heading) => {
      const text = (heading.textContent || '').trim();
      const matchesTLDR =
        text.toLowerCase().includes('tl;dr') ||
        text.toLowerCase().includes('tldr') ||
        text.toLowerCase() === 'tl;dr (60-second plan)' ||
        text.toLowerCase().match(/tl[;:]?dr/i);

      if (matchesTLDR) {
        const calloutDiv = document.createElement('div');
        calloutDiv.className = 'tldr-callout';

        const iconSpan = document.createElement('span');
        iconSpan.className = 'tldr-icon';
        iconSpan.innerHTML = '💡';

        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'tldr-content-wrapper';

        heading.parentNode?.insertBefore(calloutDiv, heading);

        const headingClone = heading.cloneNode(true) as HTMLElement;
        headingClone.className = 'tldr-heading';

        const headingContainer = document.createElement('div');
        headingContainer.className = 'tldr-heading-container';
        headingContainer.appendChild(iconSpan);
        headingContainer.appendChild(headingClone);

        contentWrapper.appendChild(headingContainer);

        let nextElement = heading.nextElementSibling;
        let hasContent = false;

        while (nextElement && nextElement.tagName !== 'H2') {
          const currentElement = nextElement.cloneNode(true) as HTMLElement;
          nextElement = nextElement.nextElementSibling;
          contentWrapper.appendChild(currentElement);
          hasContent = true;
        }

        if (!hasContent) {
          const placeholder = document.createElement('p');
          placeholder.className = 'tldr-placeholder';
          placeholder.textContent = 'Add TL;DR bullets below this heading.';
          contentWrapper.appendChild(placeholder);
        }

        calloutDiv.appendChild(contentWrapper);

        let elementToRemove = heading;
        while (elementToRemove && elementToRemove.tagName !== 'H2' || elementToRemove === heading) {
          const next = elementToRemove.nextElementSibling;
          if (elementToRemove.tagName === 'H2' && elementToRemove !== heading) break;
          elementToRemove.remove();
          if (!next || next.tagName === 'H2') break;
          elementToRemove = next as HTMLElement;
        }
      }
    });

    return tempDiv.innerHTML;
  };

  useEffect(() => {
    if (post?.content) {
      const cleaned = cleanEmptyParagraphs(post.content);
      const withIds = addHeadingIds(cleaned);
      const withTLDR = processTLDR(withIds);
      setProcessedContent(withTLDR);

      const toc = generateTOC(withIds);
      setTocItems(toc);
    }
  }, [post?.content]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return posts
      .filter(p =>
        p.id !== post.id &&
        p.status === 'published' &&
        p.tags.some(tag => post.tags.includes(tag))
      )
      .slice(0, 3);
  }, [post, posts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No date';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${post.title} - CTC Waterworks Blog`}
        description={post.excerpt}
        canonical={`https://ctcwaterworks.com/blog/${post.slug}`}
        ogImage={post.imageUrl}
      />
      <Navbar />

      <article className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-[820px] mx-auto mb-12">
            <Link
              to="/blog"
              className="inline-flex items-center text-sm text-gray-600 hover:text-ctc-blue transition-colors mb-6"
            >
              <ArrowLeft size={16} className="mr-1" />
              <span>Back to Blog</span>
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {post.title}
              </h1>

              <div className="flex items-center text-sm text-gray-500 mb-6 gap-4">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2" />
                  <span>{formatDate(post.date)}</span>
                </div>
                <div className="flex items-center">
                  <User size={16} className="mr-2" />
                  <span>{post.author}</span>
                </div>
              </div>

              <SocialShare
                url={currentUrl}
                title={post.title}
                excerpt={post.excerpt}
              />
            </motion.div>

            {post.imageUrl && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="rounded-xl overflow-hidden"
              >
                <img
                  src={post.imageUrl}
                  alt={post.title}
                  className="w-full h-auto object-cover"
                />
              </motion.div>
            )}
          </div>

          <div className="xl:grid xl:grid-cols-[280px_1fr] xl:gap-12 xl:justify-center xl:max-w-[1148px] xl:mx-auto">
            {tocItems.length > 0 && (
              <div className="hidden xl:block">
                <TableOfContents items={tocItems} />
              </div>
            )}

            <div className="max-w-[820px] mx-auto xl:mx-0">
              {tocItems.length > 0 && (
                <div className="xl:hidden mb-6">
                  <TableOfContents items={tocItems} />
                </div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="blog-content editorial-spacing"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />

              {post.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/blog?category=${encodeURIComponent(tag)}`}
                        className="inline-block px-3 py-1 rounded text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-12 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 md:p-8"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="text-center md:text-left">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Found this helpful? Let's discuss it!
                    </h3>
                    <p className="text-gray-600">
                      Share your thoughts and experiences on LinkedIn
                    </p>
                  </div>
                  <a
                    href={post.linkedin_url || 'https://www.linkedin.com/company/ctc-waterworks'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#0077B5] hover:bg-[#006399] text-white font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg whitespace-nowrap"
                  >
                    <Linkedin size={20} />
                    <span>Discuss on LinkedIn</span>
                  </a>
                </div>
              </motion.div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <SocialShare
                  url={currentUrl}
                  title={post.title}
                  excerpt={post.excerpt}
                />
              </div>
            </div>
          </div>
        </div>
      </article>

      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <motion.article
                  key={relatedPost.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <Link to={`/blog/${relatedPost.slug}`} className="block group">
                    {relatedPost.imageUrl && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={relatedPost.imageUrl}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-ctc-blue transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar size={12} className="mr-1" />
                        <span>{formatDate(relatedPost.date)}</span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
